import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  CircularProgress,
  TextField,
  InputAdornment,
  Chip,
  Alert,
  Card,
  CardContent,
  Divider,
  IconButton,
} from "@mui/material";
import { Search, Clear } from "@mui/icons-material";
import { ISelectSearchQuiz } from "interfaces";
import { AnanseApiService } from "app/services/Ananse/AnanseApiService";
import { useTranslation } from "_metronic/i18n/language";

interface QuizMultiSelectorProps {
  selectedQuizzes: ISelectSearchQuiz[];
  onSelectionChange: (quizzes: ISelectSearchQuiz[]) => void;
  disabled?: boolean;
}

const QuizMultiSelector: React.FC<QuizMultiSelectorProps> = ({
  selectedQuizzes,
  onSelectionChange,
  disabled = false,
}) => {
  const [availableQuizzes, setAvailableQuizzes] = useState<ISelectSearchQuiz[]>(
    [],
  );
  const [loadingQuizzes, setLoadingQuizzes] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");
  const translate = useTranslation();

  const api = new AnanseApiService();

  useEffect(() => {
    loadQuizzes();
  }, []);

  const loadQuizzes = async () => {
    try {
      setLoadingQuizzes(true);
      setError("");

      const response = await api.makeHttpRequest({
        method: "GET",
        url: "/quiz?Type=1&PerPage=100",
      });
      console.log("Quizzes carregados:", response);
      const quizzes = response.data.map((obj: any) => ({
        id: obj.quizId,
        value: obj.quizId,
        label: obj.name,
        obj,
      }));

      setAvailableQuizzes(quizzes);
    } catch (error) {
      console.error("Erro ao carregar quizzes:", error);
      setError("Erro ao carregar lista de quizzes. Tente novamente.");
    } finally {
      setLoadingQuizzes(false);
    }
  };

  const handleToggleQuiz = (quiz: ISelectSearchQuiz) => {
    if (disabled) return;

    const isSelected = selectedQuizzes.some(q => q.id === quiz.id);
    let newSelection: ISelectSearchQuiz[];

    if (isSelected) {
      newSelection = selectedQuizzes.filter(q => q.id !== quiz.id);
    } else {
      newSelection = [...selectedQuizzes, quiz];
    }

    onSelectionChange(newSelection);
  };

  const handleRemoveQuiz = (quizId: number) => {
    if (disabled) return;
    const newSelection = selectedQuizzes.filter(q => q.id !== quizId);
    onSelectionChange(newSelection);
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  // Filtrar quizzes baseado no termo de pesquisa
  const filteredQuizzes = availableQuizzes.filter(
    quiz =>
      quiz.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quiz.id.toString().includes(searchTerm),
  );

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {translate(
            "screens_admin_users_userCreate_quizzes",
            "Seleção de Testes",
          )}
        </Typography>

        {/* Campo de busca */}
        <TextField
          fullWidth
          size="small"
          placeholder={translate(
            "screens_admin_users_userCreate_searchQuizzes",
            "Pesquisar testes...",
          )}
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          disabled={disabled || loadingQuizzes}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={clearSearch}>
                  <Clear />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Quizzes selecionados */}
        {selectedQuizzes.length > 0 && (
          <Box mb={2}>
            <Typography variant="subtitle2" gutterBottom color="primary">
              Selecionados ({selectedQuizzes.length}):
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {selectedQuizzes.map(quiz => (
                <Chip
                  key={quiz.id}
                  label={quiz.label}
                  color="primary"
                  variant="filled"
                  size="small"
                  onDelete={
                    disabled ? undefined : () => handleRemoveQuiz(quiz.id)
                  }
                />
              ))}
            </Box>
            <Divider sx={{ mt: 2 }} />
          </Box>
        )}

        {/* Erro */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loading */}
        {loadingQuizzes ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress size={40} />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Carregando quizzes...
            </Typography>
          </Box>
        ) : (
          <>
            {/* Estatísticas */}
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {filteredQuizzes.length} quiz
              {filteredQuizzes.length !== 1 ? "es" : ""} disponível
              {filteredQuizzes.length !== 1 ? "eis" : ""}
              {searchTerm &&
                ` (filtrado${
                  filteredQuizzes.length !== 1 ? "s" : ""
                } por "${searchTerm}")`}
            </Typography>

            {/* Lista de quizzes */}
            <Box
              sx={{
                maxHeight: 300,
                overflow: "auto",
                border: 1,
                borderColor: "grey.300",
                borderRadius: 1,
                backgroundColor: "background.paper",
              }}
            >
              {filteredQuizzes.length > 0 ? (
                <List dense>
                  {filteredQuizzes.map(quiz => {
                    const isSelected = selectedQuizzes.some(
                      q => q.id === quiz.id,
                    );
                    return (
                      <ListItem key={quiz.id} disablePadding>
                        <ListItemButton
                          onClick={() => handleToggleQuiz(quiz)}
                          disabled={disabled}
                          selected={isSelected}
                        >
                          <ListItemIcon>
                            <Checkbox
                              edge="start"
                              checked={isSelected}
                              tabIndex={-1}
                              disableRipple
                              disabled={disabled}
                              color="default"
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={quiz.label}
                            secondary={`${quiz.obj.language}`}
                            primaryTypographyProps={{
                              fontWeight: isSelected ? 600 : 400,
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              ) : (
                <Box p={2} textAlign="center">
                  <Typography variant="body2" color="text.secondary">
                    {searchTerm
                      ? "Nenhum quiz encontrado para a pesquisa"
                      : "Nenhum quiz disponível"}
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default QuizMultiSelector;
